// ─── Supervisor (المشرف) — Direct Sale Page ───
// Strict mode:
//   • No cost price visible
//   • No price editing
//   • Invoices are PENDING — admin must approve before delivery
//   • Quick customer search (existing customers loaded from /supervisor/customers)
//   • Add new walk-in customer on the fly
//   • Export the invoice directly to the customer's WhatsApp after creation
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut, Search, Plus, Minus, Trash2, X,
  ShoppingCart, MessageCircle, Send, UserPlus, Phone, Store, MapPin,
  Loader2, ClipboardCheck, CheckCircle2, Package, ShieldCheck,
  Zap, Users, ChevronLeft, Building2, ZoomIn, Clock, FileText, Calendar, Pencil, RefreshCw, Wallet,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import JumlatyLogo from "../components/JumlatyLogo";
import MySalesList from "../components/supervisor/MySalesList";
import CustomerBalances from "../components/supervisor/CustomerBalances";
import MandoobHome from "../components/supervisor/MandoobHome";
import MandoobBrands from "../components/supervisor/MandoobBrands";
import MandoobCustomers from "../components/supervisor/MandoobCustomers";
import { inStockFirst } from "../utils/stockSort";
import { smartMatch } from "../utils/arabicSearch";
import TierToggle, { priceForTier, bPlusLooseSurcharge, lineTotal } from "../components/TierToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#F5A623"; // supervisor theme color
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// ─── Customer picker (smart Arabic search across saved customers) ───
const CustomerPicker = ({ customers, onPick, onAddNew }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return customers.slice(0, 30);
    return customers
      .filter((c) => smartMatch(
        `${c.full_name || ""} ${c.customer_name || ""} ${c.phone || ""} ${c.market_name || ""}`,
        q
      ))
      .slice(0, 60);
  }, [customers, q]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث باسم الزبون / رقم الهاتف / اسم الماركت..."
          data-testid="sup-customer-search"
          className="w-full pr-10 pl-4 py-3 rounded-2xl border-2 bg-white text-sm focus:outline-none"
          style={{ borderColor: ACCENT }}
        />
      </div>
      {/* Quick "+ زبون جديد" button — Feb 13, 2026: reopened per admin
          request. Lands on the AddCustomer form. */}
      <button
        type="button"
        onClick={onAddNew}
        data-testid="sup-add-customer-inline"
        className="w-full bg-white border-2 border-dashed rounded-2xl px-3 py-2.5 flex items-center justify-center gap-2 font-bold text-sm hover:bg-[#FFF8EC] transition-colors"
        style={{ borderColor: ACCENT, color: ACCENT }}
      >
        <UserPlus className="w-4 h-4" />
        إضافة زبون جديد
      </button>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-center py-6 text-sm text-[#5C6E64]">لا توجد نتائج — أو ابدأ بكتابة اسم الزبون</p>
        ) : filtered.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(c)}
            data-testid={`sup-customer-${c.id}`}
            className="w-full text-right bg-white border border-[#E4EBE6] hover:border-[var(--accent)] rounded-xl px-3 py-2 flex items-center justify-between gap-2 transition-all"
            style={{ "--accent": ACCENT }}
          >
            <div className="min-w-0">
              <p className="font-bold text-sm text-[#1E3A5F] truncate">
                {c.full_name || c.customer_name || "—"}
              </p>
              <p className="text-xs text-[#5C6E64] flex gap-2 flex-wrap" dir="ltr">
                {c.phone && <span>📞 {c.phone}</span>}
                {c.market_name && <span>🏪 {c.market_name}</span>}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Add-Customer form — used by the supervisor app to register a brand
// new customer from the field. Updated Feb 13, 2026 per admin request:
//   • Re-opened (was disabled Feb 2026 / iter 71).
//   • Now includes a price-tier selector (A+ / B+ / C+).
//   • Includes a "ربط الزبون بحسابي" checkbox — default ON. When ON the
//     server stamps `created_by_supervisor_id` so the admin always knows
//     which supervisor brought this customer in.
//   • Backend writes an activity_log entry: "🆕 إضافة زبون من المشرف:
//     [supervisor] → [customer] · ماركت: [market]".
const WalkinForm = ({ initial, onSave, onBack, isMandoob = false }) => {
  const [name, setName] = useState(initial?.full_name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [market, setMarket] = useState(initial?.market_name || "");
  const [location, setLocation] = useState(initial?.location || "");
  const [tier, setTier] = useState("A_plus");
  const [linkToMe, setLinkToMe] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || !phone.trim()) { toast.error("الاسم والهاتف مطلوبان"); return; }
    if (phone.trim().length < 7) { toast.error("رقم الهاتف غير صحيح"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/supervisor/customers`, {
        full_name: name.trim(),
        phone: phone.trim(),
        market_name: market.trim(),
        location: location.trim(),
        customer_tier: tier,
        link_to_supervisor: linkToMe,
      });
      if (data.duplicate) {
        toast.success("هذا الرقم مسجّل مسبقاً — تم اختيار الحساب القائم");
      } else {
        toast.success("تمّت إضافة الزبون");
      }
      onSave(data.customer);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر حفظ الزبون");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="sup-walkin-form">
      <div>
        <label className="text-xs text-[#5C6E64] mb-1 block">اسم الزبون *</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sup-walkin-name"
          placeholder="مثال: عبدالله علي" autoFocus
          className="w-full px-4 py-3 rounded-2xl border-2 bg-white text-sm focus:outline-none"
          style={{ borderColor: ACCENT }} />
      </div>
      <div>
        <label className="text-xs text-[#5C6E64] mb-1 block">رقم الهاتف *</label>
        <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))} dir="ltr"
          data-testid="sup-walkin-phone"
          placeholder="07XXXXXXXXX"
          className="w-full px-4 py-3 rounded-2xl border-2 bg-white text-sm focus:outline-none"
          style={{ borderColor: ACCENT }} />
      </div>
      <div>
        <label className="text-xs text-[#5C6E64] mb-1 block">اسم الماركت (اختياري)</label>
        <input value={market} onChange={(e) => setMarket(e.target.value)} data-testid="sup-walkin-market"
          placeholder="مثال: ماركت السلام"
          className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] bg-white text-sm focus:outline-none" />
      </div>
      <div>
        <label className="text-xs text-[#5C6E64] mb-1 block">العنوان (اختياري)</label>
        <input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="sup-walkin-location"
          placeholder="منطقة، شارع، علامة..."
          className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] bg-white text-sm focus:outline-none" />
      </div>
      <div className={isMandoob ? "hidden" : ""}>
        <label className="text-xs text-[#5C6E64] mb-1 block">فئة السعر</label>
        <div className="grid grid-cols-3 gap-2" data-testid="sup-walkin-tiers">
          {[
            { key: "A_plus", label: "A+", desc: "تجزئة" },
            { key: "B_plus", label: "B+", desc: "جملة" },
            { key: "C_plus", label: "C+", desc: "سي بلس" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTier(t.key)}
              data-testid={`sup-walkin-tier-${t.key}`}
              className={`py-2.5 rounded-xl border-2 font-bold text-sm transition-all ${
                tier === t.key
                  ? "text-white"
                  : "bg-white text-[#1E3A5F] border-[#E4EBE6]"
              }`}
              style={tier === t.key ? { background: ACCENT, borderColor: ACCENT } : undefined}
            >
              <span className="block text-base leading-none">{t.label}</span>
              <span className="block text-[10px] opacity-80 mt-0.5">{t.desc}</span>
            </button>
          ))}
        </div>
      </div>
      <label
        data-testid="sup-walkin-link-toggle"
        className={`${isMandoob ? "hidden" : "flex"} items-start gap-2 cursor-pointer rounded-xl p-2.5 border-2 transition-colors ${
          linkToMe
            ? "bg-[#ECFDF5] border-[#22C55E]"
            : "bg-[#F8FAFC] border-[#E4EBE6] hover:border-[#94A3B8]"
        }`}
      >
        <input
          type="checkbox"
          checked={linkToMe}
          onChange={(e) => setLinkToMe(e.target.checked)}
          data-testid="sup-walkin-link-checkbox"
          className="mt-0.5 w-4 h-4 accent-[#22C55E]"
        />
        <div className="flex-1">
          <p className="text-sm font-bold text-[#1E3A5F]">ربط الزبون بحسابي ✅</p>
          <p className="text-[11px] text-[#5C6E64] mt-0.5">
            عند التفعيل — يصير الزبون مسجّل باسمي، والإدارة تشوف إنك إنت
            اللي أضفته (يفيد للمتابعة والمكافآت).
          </p>
        </div>
      </label>
      <div className="flex gap-2">
        <button type="button" onClick={onBack} disabled={busy} data-testid="sup-walkin-back" className="flex-1 py-3 rounded-2xl bg-[#F3F6F4] text-[#5C6E64] font-bold disabled:opacity-50">رجوع</button>
        <button type="button" onClick={submit} data-testid="sup-walkin-save"
          disabled={busy}
          className="flex-[2] py-3 rounded-2xl text-white font-bold shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ background: ACCENT }}>
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري الحفظ...</> : <><UserPlus className="w-4 h-4" /> حفظ الزبون</>}
        </button>
      </div>
    </div>
  );
};

// ─── Cart Card ───
// The supervisor can tap the price to edit it INLINE — the change applies
// immediately to this customer's invoice only. The backend logs every
// override in the daily activity feed and blocks any price below cost.
const CartCard = ({ item, onPlus, onMinus, onRemove, onRequestPrice, hasApproval, lockPrice }) => {
  const overridden = !!item.is_price_override;
  return (
    <div className="bg-white rounded-xl p-2.5 border border-[#E4EBE6] flex items-center gap-2">
      {item.image_path ? (
        <img src={`${API}/files/${item.image_path}`} alt={item.name_ar}
          className="w-12 h-12 rounded-lg object-cover bg-[#F3F6F4] shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-[#F3F6F4] flex items-center justify-center shrink-0">
          <Package className="w-5 h-5 text-[#5C6E64]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-bold text-sm text-[#1E3A5F] truncate">{item.name_ar}</p>
        <div className="text-xs text-[#5C6E64] flex items-center gap-1 flex-wrap" dir="ltr">
          <button
            type="button"
            onClick={() => !lockPrice && onRequestPrice(item)}
            disabled={lockPrice}
            data-testid={`sup-cart-price-${item.id}`}
            className={`font-bold px-1 rounded ${lockPrice ? "" : "underline decoration-dotted underline-offset-2 hover:bg-yellow-50"}`}
            style={{ color: overridden ? "#B45309" : (hasApproval ? "#15803d" : ACCENT) }}
            title={lockPrice ? "السعر ثابت" : overridden ? "✎ سعر مُعدَّل يدوياً لهذا الزبون" : (hasApproval ? "✓ سعر معتمَد من الإدارة" : "اضغط لتعديل السعر لهذا الزبون فقط")}
          >
            {fmt(item.price)} {overridden ? "✎" : (hasApproval ? "✓" : "")}
          </button>
          <span>×</span>
          <span>{item.quantity}</span>
          <span>=</span>
          <b style={{ color: ACCENT }}>{fmt(item.price * item.quantity)}</b>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onMinus(item.id)} className="w-7 h-7 rounded-full bg-[#F3F6F4] flex items-center justify-center" data-testid={`sup-cart-minus-${item.id}`}>
          <Minus className="w-3 h-3" />
        </button>
        <span className="font-bold text-sm w-6 text-center" data-testid={`sup-cart-qty-${item.id}`}>{item.quantity}</span>
        <button onClick={() => onPlus(item.id)} className="w-7 h-7 rounded-full text-white flex items-center justify-center" style={{ background: ACCENT }} data-testid={`sup-cart-plus-${item.id}`}>
          <Plus className="w-3 h-3" />
        </button>
        <button onClick={() => onRemove(item.id)} className="w-7 h-7 rounded-full bg-[#FEF2F2] text-[#E85D45] flex items-center justify-center mr-1" data-testid={`sup-cart-remove-${item.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ───
const Supervisor = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(""); // "" = all
  const [zoomedProduct, setZoomedProduct] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("home"); // home | salesMode | drafts | customer | walkin | shop | success | addCustomer
  const [customer, setCustomer] = useState(null);
  const [cart, setCart] = useState({}); // {product_id: {product, quantity, price?}}
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [tier, setTier] = useState("A_plus"); // A+ (royal blue) | B+ (royal purple)
  const [approvedPrices, setApprovedPrices] = useState({}); // {product_id: {price, tier}} ← approved by admin
  const [priceRequestFor, setPriceRequestFor] = useState(null); // product whose price the supervisor wants to change
  const [cartOpen, setCartOpen] = useState(false); // 🛒 controls the full-screen cart modal (like customer app)
  // 📝 Draft (pending sale) state — persisted to backend so a supervisor can
  // resume a half-built invoice from any device / after a browser refresh.
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  // 🔒 keep the LATEST draft id in a ref so the debounced auto-save always
  // sees it — even if React hasn't re-rendered yet between rapid cart
  // additions. Fixes "5 pending drafts created instead of 1" bug.
  const currentDraftIdRef = useRef(null);
  // Guard: while an auto-save POST is in-flight, ignore further scheduled
  // saves so we can't accidentally create a second draft with the same
  // customer/cart state before the server tells us the id of the first.
  const savingRef = useRef(false);
  const isMandoob = user?.role === "mandoob";
  const accent = isMandoob ? "#1E3A8A" : ACCENT;

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    if (!["supervisor", "admin", "rep", "mandoob"].includes(user.role)) {
      toast.error("هذه الصفحة للمشرف فقط");
      navigate("/login");
      return;
    }
    (async () => {
      try {
        // Each endpoint catches its own error so a single failing call
        // never blocks the whole page — if `/supervisor/products` returns
        // 500 (e.g. transient DB hiccup) the admin still sees customers
        // + brands and gets a precise error toast telling them which
        // part failed, rather than a blank "فشل التحميل" screen.
        const [pRes, cRes, apRes, bRes] = await Promise.all([
          axios.get(`${API}/supervisor/products`).catch((e) => {
            console.error("supervisor/products failed:", e);
            toast.error("فشل تحميل المنتجات: " + (e.response?.data?.detail || e.message));
            return { data: [] };
          }),
          axios.get(`${API}/supervisor/customers`).catch((e) => {
            console.error("supervisor/customers failed:", e);
            toast.error("فشل تحميل الزبائن: " + (e.response?.data?.detail || e.message));
            return { data: [] };
          }),
          axios.get(`${API}/supervisor/my-approved-prices`).catch(() => ({ data: {} })),
          axios.get(`${API}/brands`).catch(() => ({ data: [] })),
        ]);
        setProducts(pRes.data || []);
        setCustomers(cRes.data || []);
        setApprovedPrices(apRes.data || {});
        setBrands(bRes.data || []);
      } finally { setLoading(false); }
    })();
  }, [user, navigate]);

  const reloadCustomers = async () => {
    try {
      const { data } = await axios.get(`${API}/supervisor/customers`);
      setCustomers(data || []);
    } catch (e) { /* silent */ }
  };

  // Re-poll approved prices every 15s so the supervisor sees admin approvals quickly.
  // 🎯 Include current customer_id — per-customer approvals are scoped now.
  useEffect(() => {
    if (!user || user.role !== "supervisor") return undefined;
    const id = setInterval(async () => {
      try {
        const cid = customer?.id;
        const url = cid
          ? `${API}/supervisor/my-approved-prices?customer_id=${encodeURIComponent(cid)}`
          : `${API}/supervisor/my-approved-prices`;
        const { data } = await axios.get(url);
        setApprovedPrices(data || {});
      } catch { /* ignore */ }
    }, 15000);
    return () => clearInterval(id);
  }, [user, customer?.id]);

  // Also refresh approved prices immediately when customer changes so the
  // cart never applies a price approved for a DIFFERENT customer by mistake.
  useEffect(() => {
    if (!user || user.role !== "supervisor") return;
    (async () => {
      try {
        const cid = customer?.id;
        const url = cid
          ? `${API}/supervisor/my-approved-prices?customer_id=${encodeURIComponent(cid)}`
          : `${API}/supervisor/my-approved-prices`;
        const { data } = await axios.get(url);
        setApprovedPrices(data || {});
      } catch { /* silent */ }
    })();
  }, [customer?.id, user]);

  // 📝 Fetch pending drafts (بيع معلّق) — refreshed whenever we come back
  // to `home`/`salesMode` steps so the badge count stays accurate.
  const reloadDrafts = async () => {
    try {
      const { data } = await axios.get(`${API}/supervisor/direct-sale-drafts`);
      setDrafts(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    if (!user) return;
    reloadDrafts();
  }, [user]);
  useEffect(() => {
    if (step === "home" || step === "salesMode" || step === "drafts") reloadDrafts();
  }, [step]);

  // 💾 AUTO-SAVE the current sale as a draft whenever the state materially
  // changes (customer picked, cart items changed, tier changed). Debounced
  // to 900ms so rapid clicks don't spam the backend. Doesn't run on the
  // success screen (invoice is already finalized) or when everything is
  // empty (nothing to save).
  useEffect(() => {
    if (step === "success" || step === "home" || step === "salesMode" || step === "drafts") return undefined;
    const hasCustomer = !!(customer && (customer.id || customer.phone));
    const hasItems = Object.keys(cart).length > 0;
    if (!hasCustomer && !hasItems) return undefined;
    const t = setTimeout(async () => {
      // 🔒 Guard: skip if another save is currently in flight — the pending
      // one already has the freshest state; racing it would spawn duplicates.
      if (savingRef.current) return;
      savingRef.current = true;
      try {
        const items = Object.values(cart).map((c) => ({
          product_id: c.product.id,
          quantity: c.quantity,
          price: Number(c.price ?? c.product.price ?? 0),
          name_ar: c.product.name_ar,
          image_path: c.product.image_path || "",
        }));
        const { data } = await axios.post(`${API}/supervisor/direct-sale-drafts`, {
          // Always read the ref (not the state closure) so rapid successive
          // saves collapse onto the SAME draft doc instead of creating new ones.
          id: currentDraftIdRef.current || undefined,
          customer: customer ? {
            id: customer.id, full_name: customer.full_name || customer.customer_name,
            phone: customer.phone, market_name: customer.market_name || "",
          } : {},
          tier, items,
        });
        if (data?.id) {
          currentDraftIdRef.current = data.id;
          if (!currentDraftId) setCurrentDraftId(data.id);
        }
      } catch { /* non-fatal */ }
      finally { savingRef.current = false; }
    }, 900);
    return () => clearTimeout(t);
  }, [customer, cart, tier, step]);

  // Hydrate the current sale from a saved draft. Used from the drafts list.
  const resumeDraft = (d) => {
    setCurrentDraftId(d.id);
    currentDraftIdRef.current = d.id;
    setCustomer(d.customer && (d.customer.id || d.customer.phone) ? {
      id: d.customer.id, full_name: d.customer.full_name, phone: d.customer.phone,
      market_name: d.customer.market_name,
    } : null);
    setTier(d.tier || "A_plus");
    // Rehydrate cart — try to find each item in the loaded products (for
    // fresh stock/tier logic); fall back to the snapshot in the draft.
    const nextCart = {};
    for (const it of (d.items || [])) {
      const p = products.find((x) => x.id === it.product_id) || {
        id: it.product_id, name_ar: it.name_ar, image_path: it.image_path,
        price: it.price, stock: 999,
      };
      nextCart[p.id] = { product: p, quantity: it.quantity, price: Number(it.price || 0) };
    }
    setCart(nextCart);
    setStep(d.customer && (d.customer.id || d.customer.phone) ? "shop" : "customer");
  };

  // Reset everything to a clean state (used by "بيع مباشر جديد").
  const startNewSale = () => {
    setCurrentDraftId(null);
    currentDraftIdRef.current = null;
    setCustomer(null);
    setCart({});
    setTier("A_plus");
    setStep("customer");
  };

  // Delete a saved draft (from the list). Silent success.
  const deleteDraft = async (id) => {
    try {
      await axios.delete(`${API}/supervisor/direct-sale-drafts/${id}`);
      setDrafts((ds) => ds.filter((d) => d.id !== id));
      if (currentDraftId === id) setCurrentDraftId(null);
      if (currentDraftIdRef.current === id) currentDraftIdRef.current = null;
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  const filteredProducts = useMemo(() => {
    let arr = products;
    if (selectedBrandId) arr = arr.filter((p) => p.brand_id === selectedBrandId);
    if (!q.trim()) return inStockFirst(arr);
    return inStockFirst(arr.filter((p) => smartMatch(
      `${p.name_ar || ""} ${p.name_en || p.name || ""} ${p.weight_per_piece || ""}`,
      q
    )));
  }, [products, q, selectedBrandId]);

  // Only show brands that actually have at least one product in the catalog
  const visibleBrands = useMemo(() => {
    const ids = new Set(products.map((p) => p.brand_id).filter(Boolean));
    return brands.filter((b) => ids.has(b.id));
  }, [brands, products]);

  const cartItems = useMemo(() => Object.values(cart), [cart]);
  // Cart total uses `lineTotal` so the B+ loose-pieces surcharge (+50 IQD per
  // piece for partial-carton quantities) is included automatically.
  const cartTotal = useMemo(
    () => cartItems.reduce((s, it) => s + lineTotal(it, it.quantity, tier, it.price), 0),
    [cartItems, tier]
  );
  const cartSurcharge = useMemo(
    () => cartItems.reduce((s, it) => s + bPlusLooseSurcharge(it, it.quantity, tier), 0),
    [cartItems, tier]
  );
  const cartCount = useMemo(() => cartItems.reduce((s, it) => s + it.quantity, 0), [cartItems]);

  const addToCart = (p) => {
    if ((p.stock || 0) <= 0) { toast.error("المخزون نافذ"); return; }
    // If admin has approved a custom price for this product (for this
    // supervisor), use that. Otherwise use the tier-based default.
    const approved = isMandoob ? null : approvedPrices[p.id];
    const effectivePrice = approved ? Number(approved.price) : priceForTier(p, tier);
    setCart((c) => {
      const existing = c[p.id];
      const newQty = (existing?.quantity || 0) + 1;
      if (newQty > (p.stock || 0)) { toast.error(`المخزون: ${p.stock}`); return c; }
      const price = existing ? existing.price : effectivePrice;
      // `original_price` = the tier/approved price snapshotted at add-time.
      // If the supervisor edits the row inline and the price moves away
      // from this baseline, we flip `is_price_override` to true so the
      // backend logs it in the daily activity feed (with old→new price).
      const original_price = existing ? existing.original_price : effectivePrice;
      const is_price_override = existing ? !!existing.is_price_override : false;
      return { ...c, [p.id]: { ...p, price, original_price, is_price_override, quantity: newQty } };
    });
  };
  const inc = (pid) => setCart((c) => {
    const it = c[pid]; if (!it) return c;
    if (it.quantity + 1 > (it.stock || 0)) { toast.error("المخزون نافذ"); return c; }
    return { ...c, [pid]: { ...it, quantity: it.quantity + 1 } };
  });
  const dec = (pid) => setCart((c) => {
    const it = c[pid]; if (!it) return c;
    if (it.quantity <= 1) { const n = { ...c }; delete n[pid]; return n; }
    return { ...c, [pid]: { ...it, quantity: it.quantity - 1 } };
  });
  const remove = (pid) => setCart((c) => { const n = { ...c }; delete n[pid]; return n; });
  // Re-sync cart prices whenever approvedPrices map updates (admin just approved one).
  useEffect(() => {
    queueMicrotask(() => {
      setCart((c) => {
        let changed = false;
        const next = { ...c };
        Object.keys(c).forEach((pid) => {
          const ap = approvedPrices[pid];
          if (ap && Number(ap.price) !== Number(c[pid].price)) {
            next[pid] = { ...c[pid], price: Number(ap.price) };
            changed = true;
          }
        });
        return changed ? next : c;
      });
    });
  }, [approvedPrices]);

  // 🐛 FIX (Jul 14, 2026): When the supervisor toggles A+ ↔ B+ AFTER items
  // are already in the cart, re-price every line using the new tier so
  // the cart total actually reflects the tier switch. Items with an
  // admin-approved override keep the approved price (they win over tier).
  useEffect(() => {
    setCart((c) => {
      let changed = false;
      const next = { ...c };
      Object.keys(c).forEach((pid) => {
        const item = c[pid];
        // Skip if admin has explicitly approved a custom price for this item.
        if (approvedPrices[pid]) return;
        const newPrice = priceForTier(item, tier);
        if (Number(newPrice) !== Number(item.price)) {
          next[pid] = { ...item, price: Number(newPrice) };
          changed = true;
        }
      });
      return changed ? next : c;
    });
  }, [tier]);

  const submitInvoice = async () => {
    if (cartItems.length === 0) { toast.error("السلة فارغة"); return; }
    if (!customer) { toast.error("اختر الزبون"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/supervisor/direct-sale`, {
        customer_id: customer.id || "",
        customer_name: customer.full_name || customer.customer_name,
        customer_phone: customer.phone,
        market_name: customer.market_name || "",
        tier, // A_plus | B_plus — backend uses this to compute the B+ loose-piece surcharge
        items: cartItems.map((it) => ({ product_id: it.id, quantity: it.quantity, price: it.price, is_price_override: !!it.is_price_override })),
        draft_id: currentDraftId || undefined, // 🧹 backend deletes this draft on success
      });
      setLastInvoice(data);
      setCart({});
      setCurrentDraftId(null); // draft was consumed
      currentDraftIdRef.current = null;
      setCartOpen(false); // 🛒 close the cart modal (if open) on success
      setStep("success");
      toast.success("تمّت الفاتورة وخُصمت من المخزن");
      // Play a short success chime — confirms the sale audibly even on
      // mobile devices where the green toast may auto-dismiss too fast.
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          const osc1 = ctx.createOscillator();
          const g1 = ctx.createGain();
          osc1.type = "sine";
          osc1.frequency.setValueAtTime(800, ctx.currentTime);
          osc1.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.15);
          g1.gain.setValueAtTime(0.001, ctx.currentTime);
          g1.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.04);
          g1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
          osc1.connect(g1); g1.connect(ctx.destination);
          osc1.start(); osc1.stop(ctx.currentTime + 0.5);
        }
      } catch (e) { /* ignore */ }
    } catch (err) { toast.error(err.response?.data?.detail || "فشل الإرسال"); }
    finally { setBusy(false); }
  };

  const buildWhatsappText = (inv) => {
    const lines = [];
    lines.push("🧾 *فاتورة جُملتي*");
    lines.push(`رقم الفاتورة: ${inv.invoice_number}`);
    lines.push(`الزبون: ${inv.customer_name}`);
    lines.push("");
    lines.push("───────────");
    for (const it of (inv.items || [])) {
      lines.push(`• ${it.name_ar} — ${it.quantity} × ${fmt(it.price)} = ${fmt(it.price * it.quantity)}`);
    }
    lines.push("───────────");
    lines.push(`📦 الكميات: ${inv.total_quantity}`);
    lines.push(`💰 الإجمالي: ${fmt(inv.total)} د.ع`);
    lines.push("");
    lines.push("تمّ خصم الكميّات من المخزن ✅");
    lines.push("");
    lines.push("شكراً لاختياركم جُملتي 🛒");
    return lines.join("\n");
  };

  const exportToWhatsapp = () => {
    if (!lastInvoice) return;
    let phone = (lastInvoice.customer_phone || "").trim().replace(/[^\d]/g, "");
    if (phone.startsWith("00964")) phone = phone.slice(5);
    else if (phone.startsWith("964")) phone = phone.slice(3);
    else if (phone.startsWith("0")) phone = phone.slice(1);
    const intl = `964${phone}`;
    const text = encodeURIComponent(buildWhatsappText(lastInvoice));
    window.open(`https://wa.me/${intl}?text=${text}`, "_blank");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: accent }} /></div>;
  }

  return (
    <div className="min-h-screen pb-24" dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-30 text-white shadow-lg" style={{ background: isMandoob ? "linear-gradient(135deg, #0B1E4A 0%, #1E3A8A 100%)" : `linear-gradient(135deg, ${accent} 0%, #D97706 100%)` }}>
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <JumlatyLogo size="sm" />
            <div className="min-w-0">
              <p className="font-black text-sm leading-tight truncate">{user?.full_name}</p>
              <p className="text-[10px] opacity-90 flex items-center gap-1">
                <ClipboardCheck className="w-3 h-3" />
                {isMandoob ? "المندوب" : "المشرف"}
              </p>
            </div>
          </div>
          <button onClick={logout} data-testid="sup-logout-btn"
            className="bg-white/20 hover:bg-white/30 rounded-full p-2"
            aria-label="تسجيل الخروج">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 py-4">
        {step === "home" && isMandoob && (
          <MandoobHome
            name={user?.full_name}
            onDirectSale={() => setStep("salesMode")}
            onAddCustomer={() => setStep("addCustomer")}
            onMySales={() => setStep("mySales")}
            onBrands={() => setStep("brands")}
            onCustomers={() => setStep("myCustomers")}
          />
        )}
        {step === "brands" && isMandoob && <MandoobBrands onBack={() => setStep("home")} />}
        {step === "myCustomers" && isMandoob && <MandoobCustomers onBack={() => setStep("home")} />}
        {step === "home" && !isMandoob && (
          <SupervisorHome
            customers={customers}
            draftsCount={drafts.length}
            onDirectSale={() => setStep("salesMode")}
            onAddCustomer={() => setStep("addCustomer")}
            onMySales={() => setStep("mySales")}
            onBalances={() => setStep("balances")}
          />
        )}
        {step === "balances" && (
          <CustomerBalances onBack={() => setStep("home")} />
        )}
        {step === "mySales" && (
          <MySalesList onBack={() => setStep("home")} />
        )}
        {step === "salesMode" && (
          <SalesModeChooser
            draftsCount={drafts.length}
            onNew={startNewSale}
            onOpenDrafts={() => setStep("drafts")}
            onBack={() => setStep("home")}
          />
        )}
        {step === "drafts" && (
          <DraftsList
            drafts={drafts}
            onResume={resumeDraft}
            onDelete={deleteDraft}
            onBack={() => setStep("salesMode")}
            onNew={startNewSale}
          />
        )}
        {step === "customer" && (
          <div className="space-y-3" data-testid="sup-step-customer">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-[#1E3A5F]">الخطوة 1 — اختر الزبون</h2>
              <button
                onClick={() => setStep("home")}
                data-testid="sup-back-home-btn"
                className="text-xs font-bold text-[#5C6E64] flex items-center gap-1 bg-white border border-[#E4EBE6] rounded-full px-3 py-1.5"
              >
                <ChevronLeft className="w-3 h-3" />
                الرئيسية
              </button>
            </div>
            <CustomerPicker
              customers={customers}
              onPick={(c) => { setCustomer(c); if (isMandoob) setTier(c.customer_tier || "A_plus"); setStep("shop"); }}
              onAddNew={() => setStep("addCustomer")}
            />
          </div>
        )}
        {step === "addCustomer" && (
          <div className="space-y-3" data-testid="sup-step-add-customer">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-[#1E3A5F]">إضافة زبون جديد</h2>
              <button
                onClick={() => setStep("home")}
                data-testid="sup-add-customer-back-home"
                className="text-xs font-bold text-[#5C6E64] flex items-center gap-1 bg-white border border-[#E4EBE6] rounded-full px-3 py-1.5"
              >
                <ChevronLeft className="w-3 h-3" />
                الرئيسية
              </button>
            </div>
            <WalkinForm
              isMandoob={isMandoob}
              onBack={() => setStep("home")}
              onSave={async (c) => {
                // refresh full customer list, pre-select the new customer,
                // jump directly into the sale flow so the supervisor can
                // start adding products right away.
                await reloadCustomers();
                if (c && c.id) {
                  setCustomer(c);
                  if (isMandoob) setTier("A_plus");
                  setStep("shop");
                } else {
                  setStep("home");
                }
              }}
            />
          </div>
        )}
        {step === "shop" && (
          <div className="space-y-3" data-testid="sup-step-shop">
            {/* Selected customer banner */}
            <div className="rounded-2xl p-3 text-white flex items-center justify-between" style={{ background: accent }}>
              <div>
                <p className="text-[10px] opacity-90">الزبون</p>
                <p className="font-bold text-sm">{customer?.full_name || customer?.customer_name}</p>
                <p className="text-[10px] opacity-90" dir="ltr">{customer?.phone} {customer?.market_name && `· ${customer.market_name}`}</p>
              </div>
              <button onClick={() => setStep("customer")} className="bg-white/20 rounded-full px-3 py-1 text-xs font-bold">تغيير</button>
            </div>
            {/* Tier toggle (A+ / B+) */}
            {!isMandoob && <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] flex items-center justify-between gap-3">
              <div>
                <p className="font-bold text-sm text-[#1E3A5F]">مستوى أسعار البيع</p>
                <p className="text-[10px] text-[#5C6E64]">A+ = السعر العادي · B+ = سعر الجملة</p>
              </div>
              <TierToggle value={tier} onChange={setTier} size="md" />
            </div>}
            {/* Brand (شركة) filter pills — horizontally scrollable. Choosing
                a brand filters the products grid; "الكل" clears the filter. */}
            {visibleBrands.length > 0 && (
              <div className="-mx-3 px-3" data-testid="sup-brand-filter">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  <button
                    type="button"
                    onClick={() => setSelectedBrandId("")}
                    data-testid="sup-brand-all"
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold border-2 flex items-center gap-1.5 transition-all ${
                      selectedBrandId === ""
                        ? "text-white shadow"
                        : "bg-white text-[#1E3A5F]"
                    }`}
                    style={{
                      background: selectedBrandId === "" ? accent : undefined,
                      borderColor: selectedBrandId === "" ? accent : "#E4EBE6",
                    }}
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    الكل
                  </button>
                  {visibleBrands.map((b) => {
                    const active = selectedBrandId === b.id;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setSelectedBrandId(active ? "" : b.id)}
                        data-testid={`sup-brand-${b.id}`}
                        className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold border-2 flex items-center gap-1.5 transition-all ${
                          active ? "text-white shadow" : "bg-white text-[#1E3A5F]"
                        }`}
                        style={{
                          background: active ? accent : undefined,
                          borderColor: active ? accent : "#E4EBE6",
                        }}
                      >
                        {b.logo ? (
                          <img
                            src={`${API}/files/${b.logo}`}
                            alt={b.name_ar || b.name}
                            className="w-5 h-5 rounded-full object-cover bg-white"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <Building2 className="w-3.5 h-3.5" />
                        )}
                        {b.name_ar || b.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sup-product-search"
                placeholder="ابحث عن المادة... (كبة، نص، ألف، كيلو)"
                className="w-full pr-10 pl-4 py-3 rounded-2xl border-2 bg-white text-sm focus:outline-none"
                style={{ borderColor: accent }} />
            </div>
            {/* Products list — long horizontal rows. Each row has a thumbnail
                on the right (tap to enlarge), text content on the left, and the
                whole row adds the product to the cart. Built for fast
                tablet/phone scrolling — image issues are avoided by hitting the
                `/api/files/...` route directly via `API`. */}
            <div className="space-y-2">
              {filteredProducts.length === 0 ? (
                <p className="text-center py-8 text-sm text-[#5C6E64]">لا توجد منتجات مطابقة</p>
              ) : filteredProducts.map((p) => {
                const inCart = cart[p.id];
                const out = (p.stock || 0) === 0;
                const effPrice = priceForTier(p, tier);
                return (
                  <div key={p.id}
                    data-testid={`sup-product-${p.id}`}
                    className={`bg-white border-2 rounded-2xl flex items-stretch overflow-hidden relative ${out ? "opacity-40" : ""}`}
                    style={{ borderColor: inCart ? accent : "#E4EBE6" }}>
                    {/* Right thumbnail — tap to open zoom modal */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (p.image_path) setZoomedProduct(p); }}
                      data-testid={`sup-product-zoom-${p.id}`}
                      className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 relative bg-[#F3F6F4] flex items-center justify-center"
                      aria-label="تكبير الصورة"
                    >
                      {p.image_path ? (
                        <>
                          <img
                            src={`${API}/files/${p.image_path}`}
                            alt={p.name_ar}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                          <span className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-black/55 backdrop-blur text-white flex items-center justify-center shadow">
                            <ZoomIn className="w-3 h-3" />
                          </span>
                        </>
                      ) : (
                        <Package className="w-7 h-7 text-[#5C6E64]" />
                      )}
                    </button>
                    {/* Main row — tap anywhere on the text area to add to cart */}
                    <button
                      type="button"
                      onClick={() => addToCart(p)}
                      disabled={out}
                      data-testid={`sup-product-add-${p.id}`}
                      className="flex-1 px-3 py-2 text-right disabled:cursor-not-allowed"
                    >
                      <p className="font-bold text-sm text-[#1E3A5F] leading-tight line-clamp-2">{p.name_ar}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[11px] text-[#5C6E64]" dir="ltr">
                          متوفر: <b style={{ color: out ? "#E85D45" : "#15803D" }}>{p.stock || 0}</b>
                        </span>
                        <span className="font-black text-base" style={{ color: accent }} dir="ltr">
                          {fmt(effPrice)} د.ع
                        </span>
                      </div>
                    </button>
                    {/* In-cart quantity badge */}
                    {inCart && (
                      <div className="absolute top-1.5 left-1.5 rounded-full text-white text-[10px] font-bold w-6 h-6 flex items-center justify-center shadow" style={{ background: accent }}>
                        {inCart.quantity}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {step === "success" && lastInvoice && (
          <SuccessScreen
            invoice={lastInvoice}
            onWhatsapp={exportToWhatsapp}
            onReset={() => { setLastInvoice(null); setCustomer(null); setStep("home"); }}
          />
        )}
      </div>

      {/* 🛒 Floating cart icon (like customer app) — replaces the old
          horizontal bar. Small circular button bottom-right with a red
          badge showing item count. Tap to open the full cart modal. */}
      {step === "shop" && cartItems.length > 0 && !cartOpen && (
        <motion.button
          type="button"
          onClick={() => setCartOpen(true)}
          data-testid="sup-cart-fab"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileTap={{ scale: 0.92 }}
          className="fixed bottom-5 left-5 z-30 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center text-white"
          style={{ background: `linear-gradient(135deg, ${accent} 0%, ${isMandoob ? "#0B1E4A" : "#B45309"} 100%)` }}
          aria-label={`فتح السلة (${cartCount})`}
        >
          <ShoppingCart className="w-7 h-7" strokeWidth={2.5} />
          <span
            data-testid="sup-cart-fab-badge"
            className="absolute -top-1 -right-1 min-w-[26px] h-7 px-1.5 rounded-full bg-red-500 text-white text-xs font-black flex items-center justify-center ring-2 ring-white shadow-lg animate-pulse"
            dir="ltr"
          >
            {cartCount}
          </span>
        </motion.button>
      )}

      {/* 🧺 Full cart modal — slides up from bottom, dark overlay, contains:
          A+/B+ tier switch at the top, editable cart items in the middle,
          total + send-to-admin at the bottom. Mirrors customer-app cart UX. */}
      {step === "shop" && cartOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          data-testid="sup-cart-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setCartOpen(false); }}
        >
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden"
            dir="rtl"
          >
            {/* Header: title + close */}
            <div className="px-4 py-3 flex items-center justify-between border-b-2" style={{ borderColor: accent }}>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white shadow" style={{ background: accent }}>
                  <ShoppingCart className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-black text-[#1E3A5F]">السلة</p>
                  <p className="text-[11px] text-[#5C6E64]" dir="ltr">{cartCount} قطعة · {cartItems.length} صنف</p>
                </div>
              </div>
              <button
                onClick={() => setCartOpen(false)}
                data-testid="sup-cart-modal-close"
                className="w-9 h-9 rounded-full bg-[#F3F6F4] hover:bg-[#E4EBE6] flex items-center justify-center"
                aria-label="إغلاق"
              >
                <X className="w-5 h-5 text-[#1E3A5F]" />
              </button>
            </div>
            {/* Tier switcher — royal blue A+ vs royal purple B+ */}
            {!isMandoob && <div className="px-4 py-3 border-b border-[#E4EBE6] flex items-center gap-2">
              <p className="text-xs font-bold text-[#5C6E64] shrink-0">مستوى السعر:</p>
              <div className="flex-1 grid grid-cols-2 gap-1.5">
                {[
                  { key: "A_plus", label: "A+", desc: "عادي", color: "#1E3A5F" },
                  { key: "B_plus", label: "B+", desc: "جملة", color: "#7C3AED" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTier(t.key)}
                    data-testid={`sup-cart-tier-${t.key}`}
                    className={`py-1.5 rounded-full text-xs font-black flex items-center justify-center gap-1 transition-all ${
                      tier === t.key ? "text-white shadow" : "bg-[#F3F6F4] text-[#5C6E64]"
                    }`}
                    style={{ background: tier === t.key ? t.color : undefined }}
                  >
                    {t.label} · {t.desc}
                  </button>
                ))}
              </div>
            </div>}
            {isMandoob && (
              <div className="px-4 py-2 border-b border-[#E4EBE6] text-[11px] font-bold text-[#1E3A8A] bg-blue-50" data-testid="mandoob-debt-note">
                الفاتورة تنسجل دين على الزبون · الأسعار ثابتة من الإدارة
              </div>
            )}
            {/* Cart items */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-[#FAFBFA]">
              {cartItems.map((it) => (
                <CartCard
                  key={it.id}
                  item={it}
                  onPlus={inc}
                  onMinus={dec}
                  onRemove={remove}
                  onRequestPrice={(p) => setPriceRequestFor(p)}
                  hasApproval={!isMandoob && !!approvedPrices[it.id]}
                  lockPrice={isMandoob}
                />
              ))}
            </div>
            {/* Footer: total + send */}
            <div className="px-4 py-3 border-t-2 bg-white" style={{ borderColor: accent }}>
              {cartSurcharge > 0 && (
                <div className="flex items-center justify-between text-xs text-[#7C3AED] font-bold mb-1" data-testid="sup-bplus-surcharge">
                  <span>💜 زيادة سعر B+ (50 د.ع/قطعة)</span>
                  <span dir="ltr">+ {fmt(cartSurcharge)} د.ع</span>
                </div>
              )}
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-[#5C6E64]">المجموع الكلي</span>
                <span className="font-black text-2xl" style={{ color: accent }} dir="ltr">
                  {fmt(cartTotal)} د.ع
                </span>
              </div>
              <button
                onClick={async () => { await submitInvoice(); }}
                disabled={busy}
                data-testid="sup-submit-btn"
                className="w-full py-3 rounded-2xl text-white font-black flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg"
                style={{ background: `linear-gradient(135deg, ${accent} 0%, ${isMandoob ? "#0B1E4A" : "#B45309"} 100%)` }}
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                إرسال للإدارة للموافقة
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {zoomedProduct && (
        <ProductZoomModal
          product={zoomedProduct}
          onClose={() => setZoomedProduct(null)}
        />
      )}

      {priceRequestFor && (
        <PriceRequestModal
          product={priceRequestFor}
          tier={tier}
          customer={customer}
          onApply={(pid, newPrice) => {
            // Apply the new price to the cart row IMMEDIATELY (no round-trip
            // to admin). Flag the row as an override so the backend logs
            // the change to the daily activity feed.
            setCart((c) => {
              const it = c[pid];
              if (!it) return c;
              return { ...c, [pid]: { ...it, price: Number(newPrice), is_price_override: true } };
            });
          }}
          onClose={() => setPriceRequestFor(null)}
        />
      )}
    </div>
  );
};

// ─── Price Edit Modal (Feb 28, 2026) ───
// Supervisor can NOW change the price on the fly without waiting for
// admin approval. The change applies IMMEDIATELY to this customer's
// current cart only — never to the product globally, never to other
// customers. The backend logs every change to the daily activity feed
// and blocks any price below cost_price.
const PriceRequestModal = ({ product, tier, customer, onApply, onClose }) => {
  const [draft, setDraft] = useState(String(product?.price || ""));
  const cost = Number(product?.cost_price || 0);
  const draftVal = parseFloat(draft);
  const isValid = isFinite(draftVal) && draftVal >= 0;
  const belowCost = isValid && cost > 0 && draftVal < cost;
  const submit = () => {
    if (!isValid) { toast.error("اكتب سعراً صحيحاً"); return; }
    if (belowCost) {
      toast.error(`لا يمكن — السعر تحت سعر الشراء (${fmt(cost)} د.ع)`);
      return;
    }
    onApply(product.id, draftVal);
    toast.success(`تمّ تحديث سعر «${product.name_ar}» لهذا الزبون فقط`);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-3" dir="rtl" data-testid="sup-price-request-modal">
      <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
        <div className="text-white p-4" style={{ background: "linear-gradient(135deg, #F59E0B, #E85D45)" }}>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5" />
            <h3 className="font-black text-lg">تعديل السعر</h3>
          </div>
          <p className="text-xs opacity-95">التعديل يُطبَّق فوراً على هذه الفاتورة، وتُسجَّل حركة تلقائية بالنشاط اليومي.</p>
        </div>
        <div className="p-4 space-y-3">
          {customer && (customer.full_name || customer.customer_name || customer.phone) && (
            <div
              className="rounded-xl p-3 border-2 border-dashed"
              style={{ background: "#FEF3C7", borderColor: "#F59E0B" }}
              data-testid="sup-price-request-customer-banner"
            >
              <p className="text-[10px] font-bold text-[#92400E] mb-1">📌 السعر رح يخص هذا الزبون فقط:</p>
              <p className="font-black text-sm text-[#78350F]">
                {customer.market_name || customer.full_name || customer.customer_name || "—"}
              </p>
              <p className="text-[11px] text-[#92400E]" dir="ltr">
                {customer.phone || ""}{customer.full_name || customer.customer_name ? ` · 👤 ${customer.full_name || customer.customer_name}` : ""}
              </p>
              <p className="text-[10px] text-[#92400E] mt-1">
                ⚠️ السعر الأصلي للمنتج لا يتغيّر — التعديل خاص بهذه الفاتورة فقط.
              </p>
            </div>
          )}
          <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E4EBE6] grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-[#5C6E64]">السعر الحالي</p>
              <p className="font-black text-base" style={{ color: ACCENT }} dir="ltr">{fmt(product.price)} د.ع</p>
            </div>
            <div>
              <p className="text-[10px] text-[#5C6E64]">سعر الشراء (الحد الأدنى)</p>
              <p className="font-black text-base text-[#B91C1C]" dir="ltr">{fmt(cost)} د.ع</p>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#1E3A5F] block mb-1">السعر الجديد</label>
            <input
              type="number"
              inputMode="decimal"
              lang="en"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid="sup-price-request-input"
              dir="ltr"
              className="w-full px-3 py-3 rounded-xl border-2 text-lg font-black text-center focus:outline-none focus:ring-2"
              style={{ borderColor: belowCost ? "#B91C1C" : `${ACCENT}55` }}
              placeholder="مثلاً 1800"
            />
            {belowCost && (
              <p className="text-[11px] text-[#B91C1C] font-bold mt-1 flex items-center gap-1">
                ⛔ ممنوع — السعر أقل من سعر الشراء {fmt(cost)} د.ع
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              data-testid="sup-price-request-cancel"
              className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm"
            >
              إلغاء
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!isValid || belowCost}
              data-testid="sup-price-request-submit"
              className="text-white font-bold py-2.5 rounded-full text-sm disabled:opacity-40 flex items-center justify-center gap-1"
              style={{ background: belowCost ? "#94A3B8" : ACCENT }}
            >
              <ShieldCheck className="w-4 h-4" />
              تطبيق فوراً
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Supervisor;

// ─── Product Image Zoom Modal ───
// Full-screen overlay with a large image of the product. Tap anywhere
// outside the image (or the X button) to close. The image is shown at its
// natural aspect ratio so the supervisor can verify the product visually
// before adding it to the customer's cart.
const ProductZoomModal = ({ product, onClose }) => {
  if (!product) return null;
  return (
    <div
      className="fixed inset-0 z-[140] bg-black/90 flex items-center justify-center p-4"
      dir="rtl"
      onClick={onClose}
      data-testid="sup-product-zoom-modal"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="sup-product-zoom-close"
        className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur"
        aria-label="إغلاق"
      >
        <X className="w-5 h-5" />
      </button>
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="max-w-md w-full bg-white rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {product.image_path ? (
          <img
            src={`${API}/files/${product.image_path}`}
            alt={product.name_ar}
            className="w-full aspect-square object-contain bg-[#F8FAFC]"
            data-testid="sup-product-zoom-image"
          />
        ) : (
          <div className="w-full aspect-square bg-[#F3F6F4] flex items-center justify-center">
            <Package className="w-20 h-20 text-[#5C6E64]" />
          </div>
        )}
        <div className="p-4 space-y-1 text-right">
          <p className="font-black text-lg text-[#1E3A5F] leading-tight">{product.name_ar}</p>
          {product.weight_per_piece && (
            <p className="text-xs text-[#5C6E64]" dir="ltr">{product.weight_per_piece}</p>
          )}
          <p className="font-black text-xl pt-1" style={{ color: ACCENT }} dir="ltr">
            {fmt(product.price)} د.ع
          </p>
          <p className="text-xs text-[#5C6E64]" dir="ltr">
            المتوفر: <b style={{ color: (product.stock || 0) === 0 ? "#E85D45" : "#15803D" }}>{product.stock || 0}</b>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Supervisor Home (Mobile-first) ───
// Shown as the FIRST screen when the supervisor opens the app. It mirrors a
// native mobile app layout:
//   • A "connected customers" panel at the top (live indicator dots).
//   • A clean, prominent "بيع مباشر" launcher icon below it that opens the
//     customer picker flow.
// Designed to feel like a real mobile app icon (rounded square, gradient,
// soft shadow, bouncy press animation) rather than a generic button.
const SupervisorHome = ({ customers, draftsCount = 0, onDirectSale, onAddCustomer, onMySales, onBalances }) => {
  const onlineCustomers = useMemo(
    () => (customers || []).filter((c) => c.is_online),
    [customers]
  );
  return (
    <div className="space-y-4" data-testid="sup-step-home">
      {/* ── Connected customers panel ── */}
      <div className="bg-white rounded-3xl border border-[#E4EBE6] shadow-sm overflow-hidden">
        <div
          className="px-4 py-3 flex items-center justify-between text-white"
          style={{ background: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)" }}
        >
          <div className="flex items-center gap-2">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inline-flex w-full h-full rounded-full bg-white opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-white" />
            </span>
            <p className="font-black text-sm">المتصلون بالتطبيق</p>
          </div>
          <span
            className="bg-white/25 backdrop-blur rounded-full px-3 py-0.5 font-black text-sm"
            data-testid="sup-online-count"
            dir="ltr"
          >
            {onlineCustomers.length}
          </span>
        </div>
        <div className="px-3 py-3">
          {onlineCustomers.length === 0 ? (
            <p className="text-center py-4 text-xs text-[#5C6E64]">
              لا يوجد زبائن متصلون حالياً
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="sup-online-list">
              {onlineCustomers.slice(0, 30).map((c) => (
                <div
                  key={c.id}
                  className="shrink-0 w-16 flex flex-col items-center text-center"
                  data-testid={`sup-online-${c.id}`}
                >
                  <div className="relative">
                    {c.personal_photo ? (
                      <img
                        src={`${API}/files/${c.personal_photo}`}
                        alt={c.full_name || c.customer_name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-[#16a34a] bg-[#F3F6F4]"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#F3F6F4] border-2 border-[#16a34a] flex items-center justify-center">
                        <Users className="w-5 h-5 text-[#16a34a]" />
                      </div>
                    )}
                    <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#16a34a] border-2 border-white" />
                  </div>
                  <p className="text-[10px] text-[#1E3A5F] mt-1 truncate w-full" title={c.full_name || c.customer_name}>
                    {(c.full_name || c.customer_name || "").split(" ")[0] || "—"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── App-style launcher icons grid (mobile-first) ── */}
      <div>
        <p className="text-xs font-bold text-[#5C6E64] mb-2 px-1">الأدوات</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {/* Direct Sale — the headline action (spans full row visually via larger styling) */}
          <motion.button
            type="button"
            onClick={onDirectSale}
            whileTap={{ scale: 0.92 }}
            data-testid="sup-direct-sale-icon"
            className="relative flex flex-col items-center justify-center gap-2 p-3 rounded-3xl shadow-lg text-white overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT} 0%, #D97706 60%, #B45309 100%)`,
              minHeight: 110,
            }}
          >
            {/* Decorative blob */}
            <span
              aria-hidden
              className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-25"
              style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)" }}
            />
            <span
              aria-hidden
              className="absolute -bottom-8 -left-4 w-16 h-16 rounded-full opacity-15"
              style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)" }}
            />
            <div className="relative w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
              <Zap className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <p className="relative font-black text-sm leading-tight">بيع مباشر</p>
            <span className="relative text-[9px] opacity-90 font-bold bg-white/20 rounded-full px-2 py-0.5">
              فاتورة سريعة
            </span>
            {/* 📝 Pending drafts badge — pulses if any incomplete sales exist */}
            {draftsCount > 0 && (
              <span
                className="absolute -top-2 -left-2 min-w-[24px] h-6 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center shadow-lg ring-2 ring-white animate-pulse"
                data-testid="sup-drafts-badge"
                aria-label={`${draftsCount} فاتورة معلّقة`}
              >
                {draftsCount}
              </span>
            )}
          </motion.button>

          {/* Add Customer — Feb 13, 2026: added per admin request. Same
              form as the admin AddCustomerTab + price-tier selector + a
              "ربط بحسابي" toggle so the supervisor gets credited. */}
          <motion.button
            type="button"
            onClick={onAddCustomer}
            whileTap={{ scale: 0.92 }}
            data-testid="sup-add-customer-icon"
            className="relative flex flex-col items-center justify-center gap-2 p-3 rounded-3xl shadow-lg text-white overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 60%, #0f6630 100%)",
              minHeight: 110,
            }}
          >
            <span
              aria-hidden
              className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-25"
              style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)" }}
            />
            <span
              aria-hidden
              className="absolute -bottom-8 -left-4 w-16 h-16 rounded-full opacity-15"
              style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)" }}
            />
            <div className="relative w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
              <UserPlus className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <p className="relative font-black text-sm leading-tight">إضافة زبون</p>
            <span className="relative text-[9px] opacity-90 font-bold bg-white/20 rounded-full px-2 py-0.5">
              زبون جديد
            </span>
          </motion.button>

          {/* ── قوائمي السابقة — Previous invoices with edit capability ── */}
          <motion.button
            type="button"
            onClick={onMySales}
            whileTap={{ scale: 0.92 }}
            data-testid="sup-my-sales-icon"
            className="relative flex flex-col items-center justify-center gap-2 p-3 rounded-3xl shadow-lg text-white overflow-hidden col-span-2"
            style={{
              background: "linear-gradient(135deg, #3B82F6 0%, #1E40AF 60%, #1E3A5F 100%)",
              minHeight: 90,
            }}
          >
            <span aria-hidden className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-25"
              style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)" }} />
            <div className="relative w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
              <FileText className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <p className="relative font-black text-sm leading-tight">قوائمي السابقة</p>
            <span className="relative text-[9px] opacity-90 font-bold bg-white/20 rounded-full px-2 py-0.5">
              فواتيري + تعديل غير المؤكّدة
            </span>
          </motion.button>

          <motion.button
            type="button"
            onClick={onBalances}
            whileTap={{ scale: 0.92 }}
            data-testid="sup-balances-icon"
            className="relative flex flex-col items-center justify-center gap-2 p-3 rounded-3xl shadow-lg text-white overflow-hidden col-span-2"
            style={{ background: "linear-gradient(135deg, #EF4444 0%, #B91C1C 60%, #7F1D1D 100%)", minHeight: 90 }}
          >
            <span aria-hidden className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-25"
              style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)" }} />
            <div className="relative w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
              <Wallet className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <p className="relative font-black text-sm leading-tight">أرصدة الزبائن</p>
            <span className="relative text-[9px] opacity-90 font-bold bg-white/20 rounded-full px-2 py-0.5">
              الديون — عرض فقط
            </span>
          </motion.button>
        </div>
      </div>
    </div>
  );
};

// ─── Success Screen with auto-reset countdown ───
// Shown right after a sale. Includes:
//   • Green check + invoice summary
//   • WhatsApp share button (no print — printing is admin-only by design)
//   • Auto-resets to the customer picker after 3 seconds (live countdown)
//   • User can tap "إيقاف" to cancel the auto-reset or "فاتورة جديدة" to reset immediately.
const SuccessScreen = ({ invoice, onWhatsapp, onReset }) => {
  const [seconds, setSeconds] = useState(3);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    if (seconds <= 0) {
      onReset();
      return undefined;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, paused, onReset]);
  return (
    <div className="space-y-3 max-w-md mx-auto pt-4 text-center" data-testid="sup-step-success">
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 14 }}
      >
        <CheckCircle2 className="w-20 h-20 mx-auto" style={{ color: ACCENT }} />
      </motion.div>
      <h2 className="font-black text-2xl text-[#1E3A5F]">تمّت الفاتورة ✅</h2>
      <p className="text-sm text-[#5C6E64]">رقم الفاتورة: <b dir="ltr">#{invoice.invoice_number}</b></p>
      <p className="text-sm text-[#5C6E64]">الإجمالي: <b style={{ color: ACCENT }}>{fmt(invoice.total)} د.ع</b></p>
      <p className="text-xs text-[#065F46] bg-[#ECFDF5] rounded-xl p-2.5">✅ تمّ خصم الكميّات من المخزن وتسجيل الدين على الزبون فوراً. الإدارة تشوف الفاتورة بسجلها.</p>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onWhatsapp}
          data-testid="sup-whatsapp-btn"
          className="py-3 rounded-2xl text-white font-bold flex items-center justify-center gap-2 shadow"
          style={{ background: "#25D366" }}
        >
          <MessageCircle className="w-4 h-4" />
          واتساب الزبون
        </button>
        <button
          type="button"
          onClick={onReset}
          data-testid="sup-new-invoice-btn"
          className="py-3 rounded-2xl text-white font-bold flex items-center justify-center gap-2"
          style={{ background: ACCENT }}
        >
          <Plus className="w-4 h-4" />
          فاتورة جديدة
        </button>
      </div>

      {/* Auto-reset countdown */}
      <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 flex items-center justify-between text-xs">
        <span className="text-[#5C6E64]">
          {paused ? "تم إيقاف العدّاد" : (
            <>تعود الصفحة للرئيسية بعد <b className="text-[#1E3A5F]" data-testid="sup-success-countdown" dir="ltr">{seconds}</b> ثانية</>
          )}
        </span>
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          data-testid="sup-success-pause-btn"
          className={`text-xs font-bold rounded-full px-3 py-1 ${paused ? "bg-[#15803d] text-white" : "bg-[#FEF2F2] text-[#dc2626]"}`}
        >
          {paused ? "▶ متابعة" : "⏸ إيقاف"}
        </button>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// 🔀 SalesModeChooser — two big cards: "New direct sale" vs "Pending sales".
// Shown when supervisor taps the main "بيع مباشر" icon. Pending count is
// surfaced prominently so the supervisor never forgets a half-built sale.
// ─────────────────────────────────────────────────────────────────────────────
const SalesModeChooser = ({ draftsCount, onNew, onOpenDrafts, onBack }) => (
  <div className="space-y-4" data-testid="sup-sales-mode-chooser">
    <div className="flex items-center justify-between">
      <h2 className="font-black text-lg text-[#1E3A5F]">اختر نوع البيع المباشر</h2>
      <button
        onClick={onBack}
        data-testid="sup-sales-mode-back"
        className="text-xs font-bold text-[#5C6E64] flex items-center gap-1 bg-white border border-[#E4EBE6] rounded-full px-3 py-1.5"
      >
        <ChevronLeft className="w-3 h-3" />
        الرئيسية
      </button>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {/* New sale card */}
      <motion.button
        type="button"
        whileTap={{ scale: 0.97 }}
        onClick={onNew}
        data-testid="sup-mode-new"
        className="relative rounded-3xl p-5 shadow-lg text-white text-right overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #D97706 60%, #B45309 100%)`, minHeight: 180 }}
      >
        <span aria-hidden className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-25" style={{ background: "radial-gradient(circle,#fff 0%,transparent 70%)" }} />
        <div className="relative flex flex-col items-start gap-3 h-full">
          <div className="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
            <Zap className="w-8 h-8" strokeWidth={2.5} />
          </div>
          <div className="mt-auto">
            <p className="font-black text-xl leading-tight">بيع مباشر جديد</p>
            <p className="text-xs opacity-90 mt-1">ابدأ فاتورة جديدة من الصفر</p>
          </div>
        </div>
      </motion.button>
      {/* Pending drafts card */}
      <motion.button
        type="button"
        whileTap={{ scale: 0.97 }}
        onClick={onOpenDrafts}
        disabled={draftsCount === 0}
        data-testid="sup-mode-pending"
        className={`relative rounded-3xl p-5 shadow-lg text-white text-right overflow-hidden ${draftsCount === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
        style={{ background: draftsCount > 0
          ? "linear-gradient(135deg, #E85D45 0%, #DC2626 60%, #991B1B 100%)"
          : "linear-gradient(135deg, #94A3B8 0%, #64748B 100%)", minHeight: 180 }}
      >
        <span aria-hidden className="absolute -top-8 -left-8 w-24 h-24 rounded-full opacity-25" style={{ background: "radial-gradient(circle,#fff 0%,transparent 70%)" }} />
        <div className="relative flex flex-col items-start gap-3 h-full">
          <div className="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center relative">
            <Clock className="w-8 h-8" strokeWidth={2.5} />
            {draftsCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[26px] h-7 px-2 rounded-full bg-yellow-400 text-red-900 text-xs font-black flex items-center justify-center ring-2 ring-white shadow"
                data-testid="sup-mode-pending-badge"
                dir="ltr"
              >
                {draftsCount}
              </span>
            )}
          </div>
          <div className="mt-auto">
            <p className="font-black text-xl leading-tight">فواتير معلّقة</p>
            <p className="text-xs opacity-90 mt-1">
              {draftsCount === 0
                ? "لا توجد فواتير معلّقة حالياً"
                : `${draftsCount} فاتورة بانتظار الإكمال — اضغط لعرضها`}
            </p>
          </div>
        </div>
      </motion.button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// 📋 DraftsList — shows every incomplete sale the supervisor started.
// Each row lets the supervisor RESUME (returns to shop step with cart
// re-hydrated) or DELETE the draft entirely.
// ─────────────────────────────────────────────────────────────────────────────
const DraftsList = ({ drafts, onResume, onDelete, onBack, onNew }) => {
  const relTime = (iso) => {
    if (!iso) return "—";
    try {
      const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
      if (mins < 1) return "الآن";
      if (mins < 60) return `قبل ${mins} دقيقة`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `قبل ${hrs} ساعة`;
      return `قبل ${Math.floor(hrs / 24)} يوم`;
    } catch { return "—"; }
  };
  return (
    <div className="space-y-3" data-testid="sup-drafts-list">
      <div className="flex items-center justify-between">
        <h2 className="font-black text-lg text-[#1E3A5F]">الفواتير المعلّقة ({drafts.length})</h2>
        <button
          onClick={onBack}
          data-testid="sup-drafts-back"
          className="text-xs font-bold text-[#5C6E64] flex items-center gap-1 bg-white border border-[#E4EBE6] rounded-full px-3 py-1.5"
        >
          <ChevronLeft className="w-3 h-3" /> رجوع
        </button>
      </div>
      {drafts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-[#E4EBE6]">
          <Clock className="w-14 h-14 mx-auto text-[#5C6E64] opacity-40 mb-3" />
          <p className="text-[#5C6E64] font-bold mb-4">لا توجد فواتير معلّقة</p>
          <button
            onClick={onNew}
            className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold px-5 py-2 rounded-full inline-flex items-center gap-2"
          >
            <Zap className="w-4 h-4" /> ابدأ فاتورة جديدة
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {drafts.map((d) => {
            const cust = d.customer || {};
            const itemsCount = (d.items || []).length;
            return (
              <div
                key={d.id}
                data-testid={`sup-draft-${d.id}`}
                className="bg-white border-2 border-orange-200 rounded-2xl p-3 shadow-sm hover:border-orange-400 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-[#1E3A5F] truncate">
                      {cust.market_name || cust.full_name || "بلا زبون بعد"}
                    </p>
                    {cust.phone && (
                      <p className="text-xs text-[#5C6E64] mt-0.5" dir="ltr">
                        📞 {cust.phone} · 👤 {cust.full_name || "—"}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] bg-orange-100 text-orange-700 font-bold rounded-full px-2 py-0.5 shrink-0">
                    {d.tier === "B_plus" ? "B+" : "A+"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                  <div className="bg-[#F3F6F4] rounded-lg px-2 py-1.5 text-center">
                    <p className="text-[10px] text-[#5C6E64]">أصناف</p>
                    <p className="font-black text-[#1E3A5F]">{itemsCount}</p>
                  </div>
                  <div className="bg-[#F3F6F4] rounded-lg px-2 py-1.5 text-center">
                    <p className="text-[10px] text-[#5C6E64]">إجمالي</p>
                    <p className="font-black text-[#E85D45]" dir="ltr">{fmt(d.total || 0)}</p>
                  </div>
                  <div className="bg-[#F3F6F4] rounded-lg px-2 py-1.5 text-center">
                    <p className="text-[10px] text-[#5C6E64]">آخر تعديل</p>
                    <p className="font-black text-[#1E3A5F] text-[11px]">{relTime(d.updated_at)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onResume(d)}
                    data-testid={`sup-draft-resume-${d.id}`}
                    className="flex-1 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm flex items-center justify-center gap-1.5"
                  >
                    ▶ متابعة الفاتورة
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm("حذف هذه الفاتورة المعلّقة نهائياً؟")) onDelete(d.id);
                    }}
                    data-testid={`sup-draft-delete-${d.id}`}
                    className="px-3 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

